<template>
  <BaseDropdown
    v-slot="{ id, fullButtonClasses, fullDropdownClasses, toggle, closeOnClick, close, disabled }"
    v-bind="{ ...$props }"
    @dropdown-toggled="handleDropdownToggle"
  >
    <div v-bind="wrapperAttrs" class="vue-dropdown">
      <slot v-bind="{ fullButtonClasses, id, toggle }" name="actionButton">
        <button
          ref="triggerButton"
          :data-toggle="id"
          :class="fullButtonClasses"
          :disabled="disabled"
          :aria-label="props.buttonAriaLabel"
          :aria-expanded="isOpen ? 'true' : 'false'"
          :aria-haspopup="true"
          :aria-controls="id"
          type="button"
          @click.prevent.stop="toggle"
        >
          <slot name="button" />
        </button>
      </slot>
      <ul
        v-if="isOpen"
        :id="id"
        ref="menuRef"
        :class="fullDropdownClasses"
        role="menu"
        tabindex="-1"
        :aria-labelledby="triggerButton?.id"
        @click="handleMenuClick($event, close, closeOnClick)"
        @keydown="(e) => handleKeydown(e, close)"
      >
        <slot name="listItems" v-bind="{ close }" />
      </ul>
    </div>
  </BaseDropdown>
</template>

<script setup>
import { ref, nextTick, computed, useAttrs } from 'vue'
import BaseDropdown from './dropdowns/BaseDropdown'
import useFocusTrap from 'vue_features/shared/composables/use_focus_trap'

const props = defineProps({
  buttonAriaLabel: { type: String, default: null },
})

const emit = defineEmits(['dropdown-toggled'])
const attrs = useAttrs()

const wrapperAttrs = computed(() => {
  const forwardedAttrs = { ...attrs }
  delete forwardedAttrs['aria-label']
  return forwardedAttrs
})

const menuRef = ref(null)
const triggerButton = ref(null)
const activeItem = ref(null)
const isOpen = ref(false)

const { startFocus, stopFocus } = useFocusTrap(menuRef, false, {
  escapeDeactivates: true,
  clickOutsideDeactivates: true,
  onDeactivate: () => {
    emit('dropdown-toggled', false)
    nextTick(() => triggerButton.value?.focus())
  },
})

const getFocusableItems = () => Array.from(
    menuRef.value?.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"]), input:not([disabled])') || [],
  )

const focusItem = (index) => {
  const items = getFocusableItems()
  const item = items[index]
  item?.focus()
}

const focusNextItem = () => {
  const items = getFocusableItems()
  const currentIndex = items.indexOf(document.activeElement)
  const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
  focusItem(nextIndex)
}

const focusPreviousItem = () => {
  const items = getFocusableItems()
  const currentIndex = items.indexOf(document.activeElement)
  const previousIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
  focusItem(previousIndex)
}

const setActiveItem = (item) => {
  activeItem.value = item
  getFocusableItems().forEach((i) => {
    i.tabIndex = i === item ? 0 : -1
  })
}

const handleMenuClick = (event, close, closeOnClick) => {
  const interactiveElement = event.target.closest('button, a, input')
  // closing the menu removes the form element from the DOM.
  if (interactiveElement?.closest('form')) {
    event.stopPropagation()
    return
  }
  if (interactiveElement) {
    setTimeout(() => closeOnClick && close(), 100)
  } else if (closeOnClick) {
    close()
  }
}

const handleKeydown = (event, close) => {
  if (['ArrowDown', 'ArrowUp', 'Escape'].includes(event.key)) {
    event.preventDefault()
    event.stopPropagation()
  }

  const activeElement = document.activeElement

  switch (event.key) {
    case 'Escape':
    case 'Tab':
      close()
      stopFocus()
      break
    case 'ArrowDown':
      focusNextItem()
      break
    case 'ArrowUp':
      focusPreviousItem()
      break
    case 'Enter':
    case ' ':
      if (activeElement?.tagName === 'LI') {
        const interactiveElement = activeElement.querySelector('button, a, input:not([type="hidden"])')
        ;(interactiveElement || activeElement).click()
      }
      break
  }
}

const handleDropdownToggle = (open) => {
  isOpen.value = open
  emit('dropdown-toggled', open)

  if (open) {
    nextTick(() => {
      startFocus()
      const firstMenuItem = menuRef.value?.querySelector('[role="menuitem"]:not([aria-disabled="true"])')
      if (firstMenuItem) {
        firstMenuItem.focus()
        setActiveItem(firstMenuItem)
      }
    })
  } else {
    stopFocus()
    activeItem.value = null
  }
}
</script>
